import React, { useState } from "react";
import { XLg } from "react-bootstrap-icons";
import { FullScreenModal } from "../../../libs/common/modal";
import { IMedia } from "../../../types";
import { MobileDetailModal } from "../MobileDetailModal";
import { truncateWords } from "../../../libs/utils";
import * as Styled from "./MobileSeeAllModal.styled";
import { useTheme } from "styled-components";
import { colors } from "../../../libs/themes";

interface MobileSeeAllModalProps {
    show: boolean;
    onHide: () => void;
    title: string;
    mediaItems: IMedia[];
    mediaRating: any[];
    onWatchClick: (item: IMedia) => void;
    onRatingSuccess: () => void;
    enableRatingControls: boolean;
    isSignedIn: boolean;
    activePlans: any[];
    hasFullAccess: boolean;
    freeTrialPlanIds: string[];
    sessionToken: string;
    enableCompetitionMode?: boolean;
    columns?: number;
}

export const MobileSeeAllModal: React.FC<MobileSeeAllModalProps> = ({
    show,
    onHide,
    title,
    mediaItems,
    mediaRating,
    onWatchClick,
    onRatingSuccess,
    enableRatingControls,
    isSignedIn,
    activePlans,
    hasFullAccess,
    freeTrialPlanIds,
    sessionToken,
    enableCompetitionMode,
    columns,
}) => {
    const [visibleCount, setVisibleCount] = React.useState(30);
    const [selectedItem, setSelectedItem] = useState<IMedia | null>(null);
    const theme = useTheme();

    React.useEffect(() => {
        if (show) setVisibleCount(30);
    }, [show]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        const scrollPosition = target.scrollTop + target.clientHeight;
        const threshold = 100;

        if (
            scrollPosition + threshold >= target.scrollHeight &&
            visibleCount < mediaItems.length
        ) {
            setVisibleCount((prev) => prev + 10);
        }
    };

    const hasEligiblePlan = (item: IMedia) => {
        return (
            isSignedIn &&
            (!!item.planDetails.length
                ? !!activePlans.filter((memberPlan) =>
                      item.planDetails?.some(
                          (itemPlan) => itemPlan?.id === memberPlan.id
                      )
                  ).length ||
                  freeTrialPlanIds.includes(item.planDetails[0]?.id) ||
                  hasFullAccess
                : true)
        );
    };

    return (
        <FullScreenModal
            zIndex={999999990}
            show={show}
            onHide={onHide}
            header={
                <Styled.ModalHeader>
                    <Styled.TitleContainer>
                        {title}
                        <Styled.VideoCount>
                            ({Math.min(visibleCount, mediaItems.length)} of{" "}
                            {mediaItems.length} videos)
                        </Styled.VideoCount>
                    </Styled.TitleContainer>
                    <Styled.BackArrow onClick={onHide}>
                        <XLg size={23} color={theme?.cardText ?? colors.text.black} />
                    </Styled.BackArrow>
                </Styled.ModalHeader>
            }
        >
            <Styled.ScrollableContent onScroll={handleScroll}>
                <Styled.MobileGrid columns={columns}>
                    {mediaItems.slice(0, visibleCount).map((item) => (
                        <Styled.MobileCardWrapper
                            key={item.id}
                            onClick={() => setSelectedItem(item)}
                        >
                            <Styled.ThumbnailWrapper>
                                <img src={item.thumbnail} alt={item.title} />
                            </Styled.ThumbnailWrapper>
                            <Styled.CardTitle>
                                {truncateWords(item.title, 30)}
                            </Styled.CardTitle>
                        </Styled.MobileCardWrapper>
                    ))}
                </Styled.MobileGrid>
            </Styled.ScrollableContent>

            {selectedItem && (
                <MobileDetailModal
                    isOpen={true}
                    onClose={() => setSelectedItem(null)}
                    mediaItem={selectedItem}
                    mediaRating={mediaRating.find(
                        (_item) => _item.mediaId === selectedItem.mediaId
                    )}
                    enableRatingControls={enableRatingControls}
                    onWatchClick={(item) => {
                        setSelectedItem(null);
                        onWatchClick(item);
                    }}
                    onRatingSuccess={onRatingSuccess}
                    isSignedIn={isSignedIn}
                    hasEligiblePlan={hasEligiblePlan(selectedItem)}
                    sessionToken={sessionToken}
                    enableCompetitionMode={enableCompetitionMode}
                />
            )}
        </FullScreenModal>
    );
};
